// Built in so the default remains visible before the Firestore catalog arrives.
// The authenticated API still validates every persisted provider/model choice.
export const intellikeyProvider = {
  id: 'intellikey', name: 'IntelliKey', kind: 'managed', oauthAvailable: false,
  defaultModel: 'token-packs',
  models: [{ id: 'token-packs', name: 'GPT-5.6 Luna', note: 'Google Cloud / Amazon Bedrock', recommended: true }]
}

const withIntelliKey = status => ({
  ...status,
  selectedProvider: status.selectedProvider || 'intellikey',
  selectedModels: { intellikey: 'token-packs', ...status.selectedModels },
  connections: [
    { id: 'intellikey', connected: true, status: 'available',
      ...(status.connections || status.providers || []).find(provider => provider.id === 'intellikey') },
    ...(status.connections || status.providers || []).filter(provider => provider.id !== 'intellikey')
  ]
})

export const readInferenceProviderSelection = async (me, nativeSelection, deviceOnly = false) => {
  if (deviceOnly) return withIntelliKey(await me.localOpenAISubscription('status'))
  if (await me.privateMode()) return withIntelliKey(await me.localOpenAISubscription('status'))
  const { data: server } = await me.getFunc('api/subscriptions/status')({})
  // Existing hosted subscriptions keep their current route until the user
  // explicitly switches to IntelliKey. Do not mix device Disconnect controls
  // with credentials that the server is still using.
  if (!nativeSelection || server.selectedProvider !== 'intellikey') {
    if (!nativeSelection || !me.supportsDeviceNetwork?.()) return withIntelliKey(server)
    const local = await me.localOpenAISubscription('status')
    const deviceCatalog = (local.catalog || []).filter(provider => provider.kind === 'device')
    const ids = new Set(deviceCatalog.map(provider => provider.id))
    return withIntelliKey({ ...server, deviceCatalog,
      selectedModels: { ...server.selectedModels, ...Object.fromEntries(Object.entries(local.selectedModels || {}).filter(([id]) => ids.has(id))) },
      connections: [...(server.connections || server.providers || []), ...(local.connections || []).filter(provider => ids.has(provider.id))]
    })
  }
  // Local credentials stay on the phone even while IntelliKey is selected.
  const local = await me.localOpenAISubscription('status')
  return withIntelliKey({ ...local, privateMode: false,
    connections: [...(local.connections || []).filter(provider => provider.id !== 'intellikey'),
      ...(server.connections || server.providers || []).filter(provider => provider.id === 'intellikey')],
    selectedProvider: server.selectedProvider })
}

export const usesDeviceSubscriptions = async (me, nativeSelection, privateMode, deviceOnly = false) => {
  if (deviceOnly) return true
  if (privateMode === undefined) privateMode = await me.privateMode()
  if (privateMode) return true
  if (!nativeSelection) return false
  const { data } = await me.getFunc('api/subscriptions/status')({})
  return data.selectedProvider === 'intellikey'
}

export const selectInferenceProvider = async (me, nativeSelection, provider, model, activate, deviceOnly = false) => {
  if (provider.startsWith('device-mac:')) {
    if (!me.supportsDeviceNetwork?.()) throw new Error('Update IntelliKey to use your Mac as an AI provider.')
    return withIntelliKey(await me.localOpenAISubscription('select', { provider, model, activate }))
  }
  if (deviceOnly) return withIntelliKey(await me.localOpenAISubscription('select', { provider, model, activate }))
  const privateMode = await me.privateMode()
  const uid = me.self?.uid
  if (provider === 'intellikey') {
    if (privateMode && !nativeSelection) {
      throw new Error('Update IntelliKey to switch from your on-device subscription to token packs.')
    }
    // Persist the server choice first. A failed API call must leave direct inference on.
    const { data } = await me.getFunc('api/subscriptions/select')({ provider, model, activate })
    if (me.self?.uid !== uid) throw new Error('Your account changed. Please try again.')
    if (nativeSelection && activate) {
      if (data.selectedProvider !== 'intellikey') throw new Error('Could not select IntelliKey. Please try again.')
      await me.localOpenAISubscription('select', { provider, model, activate })
      return readInferenceProviderSelection(me, nativeSelection)
    }
    return withIntelliKey(data)
  }
  if (await usesDeviceSubscriptions(me, nativeSelection, privateMode)) {
    const status = await me.localOpenAISubscription('select', { provider, model, activate })
    // Saving a model for a disconnected provider must not change the active route.
    return activate ? withIntelliKey(status) : readInferenceProviderSelection(me, nativeSelection)
  }
  const { data } = await me.getFunc('api/subscriptions/select')({ provider, model, activate })
  return withIntelliKey(data)
}
